import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { roleLabel } from "@/features/organization-members/member-change-copy";
import { notifyError } from "@/lib/notify-error";
import { cn } from "@/lib/utils";

const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/** Company name, the viewer's role, and the logo shown in the app and report headers. */
export function OrganizationGeneralPanel(props: {
  organizationName: string | null | undefined;
  viewerRole: string | undefined;
  canManageOrg: boolean;
}) {
  const { data: branding } = useQuery(convexQuery(api.organizations.getBranding, {}));
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);
  const updateBranding = useMutation(api.organizations.updateBranding);
  const removeLogo = useMutation(api.organizations.removeLogo);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const displayName = branding?.organizationName ?? props.organizationName ?? "Organization";
  const isBusy = isUploadingLogo || isRemovingLogo;

  const handleLogoUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Upload an image file for the organization logo");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 5 MB or smaller");
      return;
    }

    setIsUploadingLogo(true);
    try {
      const { key, url } = await generateR2UploadUrl({
        prefix: "organization-branding/logos",
        filename: file.name,
      });
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) {
        throw new Error("Logo upload failed");
      }
      await updateBranding({ logoFileKey: key, logoMimeType: file.type });
      toast.success("Organization logo updated");
    } catch (error) {
      console.error("Logo upload error:", error);
      notifyError(error, error instanceof Error ? error.message : "Failed to update logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setIsRemovingLogo(true);
    try {
      await removeLogo({});
      toast.success("Organization logo removed");
    } catch (error) {
      console.error("Logo remove error:", error);
      notifyError(error, error instanceof Error ? error.message : "Failed to remove logo");
    } finally {
      setIsRemovingLogo(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company</CardTitle>
        <CardDescription>Your logo appears in the app and in generated report headers.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex size-20 items-center justify-center rounded-md border-2 bg-muted/40">
            {branding?.logoUrl ? (
              <img src={branding.logoUrl} alt={`${displayName} logo`} className="max-h-16 max-w-16 object-contain" />
            ) : (
              <span className="text-xs font-semibold uppercase text-muted-foreground">No Logo</span>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate text-base font-semibold">{displayName}</p>
            <p className="text-sm text-muted-foreground">Your role: {roleLabel(props.viewerRole)}</p>
            {props.canManageOrg ? (
              <p className="text-sm text-muted-foreground">PNG, JPG, or SVG, up to 5 MB.</p>
            ) : null}
          </div>
          {props.canManageOrg ? (
            <div className="flex flex-wrap gap-2">
              {/* A label can't be disabled, so the busy guard sits on the file input itself. */}
              <Button asChild variant="outline">
                <label
                  aria-disabled={isBusy || undefined}
                  className={cn(
                    "cursor-pointer focus-within:ring-2 focus-within:ring-ring",
                    isBusy && "pointer-events-none opacity-50"
                  )}
                >
                  {isUploadingLogo ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={isBusy}
                    onChange={(event) => {
                      void handleLogoUpload(event.currentTarget.files?.[0]);
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
              </Button>
              {branding?.logoFileKey ? (
                <Button type="button" variant="outline" onClick={() => void handleRemoveLogo()} disabled={isBusy}>
                  {isRemovingLogo ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                  Remove
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
